'use client';

import { useLayoutEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { matchesHeaderHidePathname } from '@/logic/layout/hide-header-on-route';

/**
 * Keeps `<body>` header/ribbon classes and store visibility aligned with `matchesHeaderHidePathname`
 * on client navigations (hide on matching paths, restore when leaving).
 */
export const HeaderVisibilityRouteSync = () => {
    const pathname = usePathname() ?? '/';
    const { isMobile, isTablet, isAndroidWrapper, isIosWrapper, isIos } = useGlobalContext();
    const { headerVisible, ribbonVisible, setHeaderVisible, setRibbonVisible } = useStore((s) => s.header);
    const hidHeaderForRouteRef = useRef(false);

    const isNarrowDevice = isMobile || isTablet || isAndroidWrapper;

    useLayoutEffect(() => {
        const isHeaderHideRoute = matchesHeaderHidePathname(pathname, isIos, isIosWrapper);
        const { body } = document;
        const bodyHasHideClasses = body.classList.contains('header-hidden') && body.classList.contains('ribbon-hidden');
        const shouldHide = isHeaderHideRoute && (isNarrowDevice || bodyHasHideClasses);

        if (shouldHide) {
            hidHeaderForRouteRef.current = true;
            if (isNarrowDevice && !bodyHasHideClasses) {
                body.classList.add('header-hidden', 'ribbon-hidden');
            }
            if (headerVisible) {
                setHeaderVisible(false);
            }
            if (ribbonVisible) {
                setRibbonVisible(false);
            }
            return;
        }

        if (hidHeaderForRouteRef.current) {
            hidHeaderForRouteRef.current = false;
            body.classList.remove('header-hidden', 'ribbon-hidden');

            if (!headerVisible) {
                setHeaderVisible(true);
            }
            if (!ribbonVisible) {
                setRibbonVisible(true);
            }
        }
    }, [
        isNarrowDevice,
        pathname,
        headerVisible,
        ribbonVisible,
        setHeaderVisible,
        setRibbonVisible,
        isIos,
        isIosWrapper
    ]);

    return null;
};
